import { WaterfallChart, ScoreCard, WhiteBox } from "@foodpilot/foods";
import { Stack, useTheme, useMediaQuery } from "@mui/material";
import { t } from "i18next";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import {
    useGetAllActionPlanActions,
    ActionCsrTheme,
    ActionPlanActionList,
} from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { EmptyCard } from "./EmptyCard";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import {
    mapVariationsToScoreCardOptions,
    useActionPlanSelectorVariations,
} from "src/hooks/useActionPlanSelectorVariations";
import { Suspense } from "src/components/utils/Suspense";
import { useLanguage } from "src/context/LanguageProvider";
import { formatImpact } from "src/context/Scores/utils/impactDisplay";

export const ScoreWaterfall = () => {
    const { lastSelectedVariation, selectedScorePrecision, scoresWithVariations } = useEntityScores();
    const selectorVariations = useActionPlanSelectorVariations(scoresWithVariations);
    const { persistedOptions, actionPlan, isFetched, olapScoresQuery } = useActionsContext();
    const theme = useTheme();
    const { currentLanguage } = useLanguage();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Validate required parameters
    const actionPlanId = actionPlan?.id;

    const {
        data: allActionPlanActions,
        isLoading: isApiLoading,
        error: apiError,
    } = useGetAllActionPlanActions({
        actionPlanID: actionPlanId,
        scoresQuery: olapScoresQuery,
    });

    const variationsOptions = mapVariationsToScoreCardOptions(selectorVariations);

    const scoreId = lastSelectedVariation.scoreId;

    // Show loading state when parameters are missing or API is loading
    if (!actionPlanId || !scoreId || isApiLoading) {
        return (
            <WhiteBox>
                <Stack>
                    <ScoreCard
                        header={{
                            selectedItemId: lastSelectedVariation.rowId,
                            options: variationsOptions,
                            variant: "secondary",
                            orientation: "horizontal",
                        }}
                        disableFooter={true}
                    >
                        <EmptyCard
                            title={t("common.messages.loading")}
                            subtitle={t("action_plan.preview.loading_comparison_data", "Loading comparison data...")}
                        />
                    </ScoreCard>
                </Stack>
            </WhiteBox>
        );
    }

    // Handle API error
    if (apiError) {
        return (
            <WhiteBox>
                <Stack>
                    <ScoreCard
                        header={{
                            selectedItemId: lastSelectedVariation.rowId,
                            options: variationsOptions,
                            variant: "secondary",
                            orientation: "horizontal",
                        }}
                        disableFooter={true}
                    >
                        <EmptyCard
                            title={t("actionplan.preview.error.title")}
                            subtitle={`${t("actionplan.preview.error.description")} (${(apiError as any)?.response?.status || t("unknown", "Unknown")}: ${(apiError as any)?.message || t("unknown_error")})`}
                        />
                    </ScoreCard>
                </Stack>
            </WhiteBox>
        );
    }

    const startValue = OlapScoresService.normalizeNumericalScore(
        actionPlan.startSsqPost.olapScores[scoreId]?.score.value,
    );

    // Handle no data response
    if (!allActionPlanActions || startValue === null) {
        return (
            <WhiteBox>
                <Stack>
                    <ScoreCard
                        header={{
                            selectedItemId: lastSelectedVariation.rowId,
                            options: variationsOptions,
                            variant: "secondary",
                            orientation: "horizontal",
                        }}
                        disableFooter={true}
                    >
                        <EmptyCard
                            title={t("action_plan.preview.no_comparison_data", "No comparison data")}
                            subtitle={t(
                                "action_plan.preview.no_comparison_data.description",
                                "The API returned no comparison data for this action plan and score.",
                            )}
                        />
                    </ScoreCard>
                </Stack>
            </WhiteBox>
        );
    }

    const startPost = actionPlan.startSsqPost;
    const differences = extractWaterfallData(allActionPlanActions ?? [], scoreId);

    const start = {
        label: startPost.campaign?.label || "",
        value: startValue,
    };

    const target = {
        label: persistedOptions.withPlanName.toLowerCase(),
    };

    return (
        <WhiteBox
            sx={{
                ...(isMobile && {
                    border: "none",
                }),
            }}
        >
            <Stack>
                <ScoreCard
                    header={{
                        selectedItemId: lastSelectedVariation.rowId,
                        options: variationsOptions,
                        variant: "secondary",
                        orientation: isMobile ? "vertical" : "horizontal",
                    }}
                    disableFooter={true}
                >
                    {differences.length === 0 ?
                        <EmptyCard
                            title={t("actionplan.waterfall.no_data.title", "No waterfall data available")}
                            subtitle={t(
                                "actionplan.waterfall.no_data.subtitle",
                                "You can add actions to the action plan to see the waterfall chart.",
                            )}
                        />
                    :   <Suspense isLoading={!isFetched}>
                            <WaterfallChart
                                reference={start}
                                height={isMobile ? "420px" : "600px"}
                                width="100%"
                                improvement={lastSelectedVariation.improvement}
                                unit={lastSelectedVariation.unit}
                                values={differences.map((d) => {
                                    return {
                                        ...d,
                                        value: d.difference ?? 0,
                                    };
                                })}
                                target={target}
                                forcedPrecision={lastSelectedVariation.precision}
                                textOptions={{
                                    setOfActionsLabel: t("actionplan.common.allactions"),
                                    goalProgressLabelDescription: t("actionplan.common.actionsprogresslabel"),
                                }}
                                valueFormatterFn={(value) => {
                                    return formatImpact(value, selectedScorePrecision, currentLanguage);
                                }}
                            />
                        </Suspense>
                    }
                </ScoreCard>
            </Stack>
        </WhiteBox>
    );
};

type WaterfallData = {
    id: number;
    label: string;
    difference: number;
};

const extractWaterfallData = (actionPlanActions: ActionPlanActionList[], scoreId: number): WaterfallData[] => {
    const waterfallData = actionPlanActions.reduce((acc, actionPlanAction) => {
        const action = actionPlanAction.action;
        const csrThemes = action.csrThemes;

        return getAggregatedCsrThemesData(acc, actionPlanAction, scoreId, csrThemes[0] ?? undefined);
    }, {});

    return Object.values(waterfallData);
};

const getAggregatedCsrThemesData = (
    data: Record<number, WaterfallData>,
    actionPlanAction: ActionPlanActionList,
    scoreId: number,
    theme?: ActionCsrTheme,
): Record<number, WaterfallData> => {
    const csrThemeId = theme?.id ?? 0;
    const totalImpact = !Array.isArray(actionPlanAction.totalImpact) ? actionPlanAction.totalImpact : {};

    if (data[csrThemeId] === undefined) {
        data[csrThemeId] = {
            id: theme?.id ?? 0,
            label: theme?.title ?? t("action_plan.action.not_categorized", "Not categorized"),
            difference: 0,
        };
    }

    data[csrThemeId].difference += totalImpact[scoreId] ?? 0;

    return data;
};
